import { useMemo } from 'react';

import { InsightsCatalogLayer } from '@/endpoints/insights-catalog/getInsightsCatalogLayers';
import { County } from '@/endpoints/itasca/county/getCounties';
import { OpenSearchParcel } from '@/endpoints/itasca/parcels/search';
import { State } from '@/endpoints/itasca/state/getState';
import { SectionTownshipRange } from '@/endpoints/township-range/getSectionTownshipRange';
import { TownshipRange } from '@/endpoints/township-range/getTownshipRange';
import { SavedSelection } from '@/models/land';
import { IPlaceFeature } from '@/models/search';

import {
  IndexedSearchResult,
  IndexedURLSearchResult,
  SearchResult,
  SearchResultType,
} from '../models';
import { useCountySearch } from './useCountySearch';
import { useLayersSearch } from './useLayersSearch';
import { useLonLatSearch } from './useLonLatSearch';
import { useParcelNumberSearch } from './useParcelNumberSearch';
import { useParcelOwnerAddressSearch } from './useParcelOwnerAddressSearch';
import { useParcelOwnerNameSearch } from './useParcelOwnerNameSearch';
import { usePlaceSearch } from './usePlaceSearch';
import { useSavedMapsSearch } from './useSavedMapsSearch';
import { useSectionTownshipRangeSearch } from './useSectionTownshipRangeSearch';
import { useStateSearch } from './useStateSearch';
import { useTownshipRangeSearch } from './useTownshipRangeSearch';

type ResponseType = {
  /** The TOTAL results, not segmented into each type */
  searchResults: IndexedSearchResult<any>[];
  /** Same as the searchResults but only the lon lat results */
  lonLatResults: IndexedSearchResult<[number, number]>[];
  /** Same as the searchResults but only the mapbox places results */
  placesResults: IndexedSearchResult<IPlaceFeature>[];
  placesLoading: boolean;
  /** Same as the searchResults but only the county results */
  countyResults: IndexedSearchResult<County>[];
  countyLoading: boolean;
  /** Same as the searchResults but only the state results */
  stateResults: IndexedSearchResult<State>[];
  stateLoading: boolean;
  /** Same as the searchResults but only the township results */
  townshipResults: IndexedSearchResult<TownshipRange>[];
  townshipLoading: boolean;
  /** Same as the searchResults but only the section township range results */
  sectionTownshipRangeResults: IndexedSearchResult<SectionTownshipRange>[];
  sectionTownshipRangeLoading: boolean;
  /** Same as the searchResults but only the layers results */
  layersResults: IndexedURLSearchResult<InsightsCatalogLayer>[];
  layersLoading: boolean;
  layersSeeMore: number;
  /** Same as the searchResults but only the saved maps results */
  savesResults: IndexedURLSearchResult<SavedSelection>[];
  savesLoading: boolean;
  savesSeeMore: number;
  /** Same as the searchResults but only the parcel owners results */
  parcelOwnersResults: IndexedURLSearchResult<OpenSearchParcel>[];
  parcelOwnersLoading: boolean;
  parcelOwnersSeeMore: number;
  /** Same as the searchResults but only the parcel owner addresses results */
  parcelOwnerAddressesResults: IndexedURLSearchResult<OpenSearchParcel>[];
  parcelOwnerAddressLoading: boolean;
  parcelOwnerAddressesSeeMore: number;
  /** Same as the searchResults but only the parcel numbers results */
  parcelNumberResults: IndexedURLSearchResult<OpenSearchParcel>[];
  parcelNumberLoading: boolean;
  parcelNumbersSeeMore: number;

  areAnyResultsLoading: boolean;
};

export const useSearchResults = (debouncedInput: string): ResponseType => {
  const { places, loading: placesLoading } = usePlaceSearch(debouncedInput);
  const lonLat = useLonLatSearch(debouncedInput);
  const { townships, loading: townshipLoading } =
    useTownshipRangeSearch(debouncedInput);
  const { sectionTownshipRanges, loading: sectionTownshipRangeLoading } =
    useSectionTownshipRangeSearch(debouncedInput);
  const { counties, loading: countyLoading } = useCountySearch(debouncedInput);
  const { states, loading: stateLoading } = useStateSearch(debouncedInput);
  const {
    layers,
    layersSeeMore,
    isLoading: layersLoading,
  } = useLayersSearch(debouncedInput, {});
  const {
    saves,
    savesSeeMore,
    isLoading: savesLoading,
  } = useSavedMapsSearch(debouncedInput);
  const {
    parcelOwners,
    parcelOwnersSeeMore,
    loading: parcelOwnersLoading,
  } = useParcelOwnerNameSearch(debouncedInput);
  const {
    parcelOwnerAddresses,
    parcelOwnerAddressesSeeMore,
    loading: parcelOwnerAddressLoading,
  } = useParcelOwnerAddressSearch(debouncedInput);
  const {
    parcelNumbers,
    parcelNumbersSeeMore,
    loading: parcelNumberLoading,
  } = useParcelNumberSearch(debouncedInput);

  const areAnyResultsLoading = useMemo(() => {
    if (!debouncedInput) return false;
    return (
      placesLoading ||
      townshipLoading ||
      sectionTownshipRangeLoading ||
      countyLoading ||
      stateLoading ||
      layersLoading ||
      savesLoading ||
      parcelOwnersLoading ||
      parcelOwnerAddressLoading ||
      parcelNumberLoading
    );
  }, [
    debouncedInput,
    placesLoading,
    townshipLoading,
    sectionTownshipRangeLoading,
    countyLoading,
    stateLoading,
    layersLoading,
    savesLoading,
    parcelOwnersLoading,
    parcelOwnerAddressLoading,
    parcelNumberLoading,
  ]);

  const searchResults: IndexedSearchResult<any>[] = useMemo(() => {
    return (
      [
        ...counties,
        ...states,
        lonLat,
        ...townships,
        ...sectionTownshipRanges,
        ...places,
        ...parcelNumbers,
        ...layers,
        ...saves,
        ...parcelOwners,
        ...parcelOwnerAddresses,
      ].filter((x) => x !== null) as SearchResult<any>[]
    ).map((x, i) => ({
      ...x,
      index: i,
    }));
  }, [
    lonLat,
    counties,
    states,
    places,
    townships,
    sectionTownshipRanges,
    parcelNumbers,
    layers,
    saves,
    parcelOwners,
    parcelOwnerAddresses,
  ]);

  const lonLatResults: IndexedSearchResult<[number, number]>[] = useMemo(() => {
    if (!searchResults) return [];

    return searchResults.filter((x) => x.type === SearchResultType.GO_TO_POINT);
  }, [searchResults]);

  const placesResults: IndexedSearchResult<IPlaceFeature>[] = useMemo(() => {
    if (!searchResults) return [];

    return searchResults.filter(
      (x) => x.type === SearchResultType.MAPBOX_PLACE
    );
  }, [searchResults]);

  const townshipResults: IndexedSearchResult<TownshipRange>[] = useMemo(() => {
    if (!searchResults) return [];

    return searchResults.filter(
      (x) => x.type === SearchResultType.TOWNSHIP_RANGE
    );
  }, [searchResults]);

  const sectionTownshipRangeResults: IndexedSearchResult<SectionTownshipRange>[] =
    useMemo(() => {
      if (!searchResults) return [];

      return searchResults.filter(
        (x) => x.type === SearchResultType.SECTION_TOWNSHIP_RANGE
      );
    }, [searchResults]);

  const countyResults: IndexedSearchResult<County>[] = useMemo(() => {
    if (!searchResults) return [];

    return searchResults.filter((x) => x.type === SearchResultType.COUNTY);
  }, [searchResults]);

  const stateResults: IndexedSearchResult<State>[] = useMemo(() => {
    if (!searchResults) return [];

    return searchResults.filter((x) => x.type === SearchResultType.STATE);
  }, [searchResults]);

  const layersResults: IndexedURLSearchResult<InsightsCatalogLayer>[] =
    useMemo(() => {
      if (!searchResults) return [];

      return searchResults.filter(
        (x) => x.type === SearchResultType.LAYER_LIBRARY
      ) as IndexedURLSearchResult<InsightsCatalogLayer>[];
    }, [searchResults]);

  const savesResults: IndexedURLSearchResult<SavedSelection>[] = useMemo(() => {
    if (!searchResults) return [];

    return searchResults.filter(
      (x) => x.type === SearchResultType.SAVED_MAP
    ) as IndexedURLSearchResult<SavedSelection>[];
  }, [searchResults]);

  const parcelOwnersResults: IndexedURLSearchResult<OpenSearchParcel>[] =
    useMemo(() => {
      if (!searchResults) return [];

      return searchResults.filter(
        (x) => x.type === SearchResultType.PARCEL_OWNER_NAME
      ) as IndexedURLSearchResult<OpenSearchParcel>[];
    }, [searchResults]);

  const parcelOwnerAddressesResults: IndexedURLSearchResult<OpenSearchParcel>[] =
    useMemo(() => {
      if (!searchResults) return [];

      return searchResults.filter(
        (x) => x.type === SearchResultType.PARCEL_OWNER_ADDRESS
      ) as IndexedURLSearchResult<OpenSearchParcel>[];
    }, [searchResults]);

  const parcelNumberResults: IndexedURLSearchResult<OpenSearchParcel>[] =
    useMemo(() => {
      if (!searchResults) return [];

      return searchResults.filter(
        (x) => x.type === SearchResultType.PARCEL_NUMBER
      ) as IndexedURLSearchResult<OpenSearchParcel>[];
    }, [searchResults]);

  return {
    areAnyResultsLoading,
    countyLoading,
    countyResults,
    layersLoading,
    layersResults,
    layersSeeMore,
    lonLatResults,
    parcelNumberLoading,
    parcelNumberResults,
    parcelNumbersSeeMore,
    parcelOwnerAddressLoading,
    parcelOwnerAddressesResults,
    parcelOwnerAddressesSeeMore,
    parcelOwnersLoading,
    parcelOwnersResults,
    parcelOwnersSeeMore,
    placesLoading,
    placesResults,
    savesLoading,
    savesResults,
    savesSeeMore,
    searchResults,
    sectionTownshipRangeLoading,
    sectionTownshipRangeResults,
    stateLoading,
    stateResults,
    townshipLoading,
    townshipResults,
  };
};
